"use client";

import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import LogoUploader from "../../sections/header/LogoUploader";
import LoginDropdown from "../../sections/header/LoginDropdown";
import {
  PiListBold,
  PiXBold,
  PiMagnifyingGlass,
  PiGlobe,
  PiCaretDownBold,
  PiUserBold,
  PiShoppingCartBold,
  PiMicrophoneBold,
  PiChatCircleTextBold,
  PiMapPin,
  PiPhone,
  PiEnvelope,
  PiArrowSquareOutBold,
} from "react-icons/pi";

export default function HokHeader({
  content,
  onContentChange,
  isSelected,
  defaultLogo,
}: SectionContentProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [searchQuery, setSearchQuery] = useState("");

  const primaryColor = content.colorPalette?.primary || "#684DF4"; // Purple Theme
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const padding = content.padding || "normal";
  const getPaddingClass = () => {
    if (padding === "compact") return "py-1.5";
    if (padding === "spacious") return "py-5";
    return "py-2.5";
  };

  const handleLogoChange = (newLogoUrl: string) => {
    if (onContentChange) {
      onContentChange({ ...content, logo: newLogoUrl });
    }
  };

  const navItemsRaw = content.navItems || [
    { label: "Home", sectionId: "hero-default" },
    { label: "Courses", sectionId: "courses-default" },
    { label: "Instructors", sectionId: "instructors-default" },
    { label: "Learning Hub", sectionId: "learning-default" },
    { label: "Blog", sectionId: "blog-default" },
    { label: "About", sectionId: "about-default" },
    { label: "Support", sectionId: "support-default" },
  ];

  return (
    <>
      <div
        className={`w-full transition-all bg-white text-gray-900 border-b border-gray-100 ${
          isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""
        }`}
        style={{ fontFamily }}
      >
        {/* Upper Row: Logo, Search, Quick Actions */}
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-4 border-b border-gray-50 ${getPaddingClass()}`}
        >
          {/* Logo */}
          <div className="flex-shrink-0">
            <LogoUploader
              currentLogo={content.logo}
              defaultLogo={defaultLogo}
              onLogoChange={handleLogoChange}
              heightClass="h-10"
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              companyName={content.companyName || "HOK"}
              isEditing={!!onContentChange}
            />
          </div>

          {/* Search Bar Wrapper */}
          {content.showSearch !== false && (
            <div className="flex-1 max-w-2xl w-full flex items-center bg-gray-50 border border-gray-200 rounded-lg overflow-hidden transition-all focus-within:border-purple-300 focus-within:ring-1 focus-within:ring-purple-200">
              {/* Category Dropdown Selector */}
              <div className="relative flex-shrink-0 border-r border-gray-200 px-4 py-2 flex items-center gap-1.5 cursor-pointer bg-white text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50">
                <span>
                  <EditableText
                    value={content.categoryPlaceholder || "All Categories"}
                    onChange={(val) => onContentChange?.({ ...content, categoryPlaceholder: val })}
                    as="span"
                    placeholder="All Categories"
                  />
                </span>
                <PiCaretDownBold className="w-3 h-3 text-gray-400" />
              </div>

              {/* Input fields */}
              <div className="flex-1 flex items-center px-3 relative">
                <PiMagnifyingGlass className="w-4 h-4 text-gray-400 mr-2" />
                <input
                  type="text"
                  placeholder={content.searchPlaceholder || "Search for courses, topics, or instructors..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full py-2 bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
                />
              </div>

              {/* Microphone/Search Button */}
              <button
                className="p-2.5 text-white flex items-center justify-center transition-all hover:opacity-90"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                }}
                aria-label="Search or voice input"
              >
                <PiMicrophoneBold className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Quick Actions (My Account) */}
          <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
            {/* My Account */}
            {content.showAccount !== false && (
              <LoginDropdown
                variant="text"
                className="flex items-center gap-1.5 px-4 py-1.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white shadow-sm cursor-pointer hover:bg-gray-50 hover:text-gray-900"
              />
            )}

            {/* Mobile Menu Icon */}
            <button
              className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <PiListBold className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Lower Row: Navigation and Help Button */}
        <div className="hidden lg:block border-t border-gray-100 bg-white py-1.5">
          <div className="max-w-7xl mx-auto px-8 flex items-center justify-between">
            {/* Nav Menu */}
            <nav className="flex items-center gap-8">
              {navItemsRaw.map((item: any, idx: number) => {
                const navItem =
                  typeof item === "string"
                    ? {
                        label: item,
                        sectionId: item.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
                      }
                    : item;

                const isCustomLink = navItem.sectionId === "custom";
                const targetHref = isCustomLink ? navItem.url : `#${navItem.sectionId}`;
                const isEditing = !!onContentChange;

                return (
                  <a
                    key={idx}
                    href={isEditing ? undefined : targetHref}
                    target={isCustomLink && !isEditing ? "_blank" : undefined}
                    rel={isCustomLink ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                      if (isEditing) {
                        e.preventDefault();
                      } else if (!isCustomLink) {
                        e.preventDefault();
                        const el = document.getElementById(navItem.sectionId);
                        if (el) {
                          el.scrollIntoView({ behavior: "smooth" });
                        }
                      }
                    }}
                  >
                    <EditableText
                      value={navItem.label}
                      onChange={(val) => {
                        const newItems = [...(content.navItems || [])];
                        newItems[idx] = { ...navItem, label: val };
                        if (onContentChange)
                          onContentChange({ ...content, navItems: newItems });
                      }}
                      className="text-[13px] font-bold tracking-wider uppercase text-gray-800 hover:text-purple-600 cursor-pointer transition-colors"
                      as="span"
                      placeholder="NAV"
                    />
                  </a>
                );
              })}
            </nav>

            {/* Help & Support Button */}
            {content.showCta !== false && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (!onContentChange) setIsSupportModalOpen(true);
                }}
                className="flex items-center gap-2 rounded px-4 py-1.5 text-xs font-bold text-white shadow-sm transition-all hover:shadow hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                }}
              >
                <PiChatCircleTextBold className="w-4 h-4 text-white" />
                <EditableText
                  value={content.ctaText || "Student Support"}
                  onChange={(val) =>
                    onContentChange && onContentChange({ ...content, ctaText: val })
                  }
                  className="text-white"
                  as="span"
                  placeholder="Student Support"
                />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-white flex flex-col lg:hidden"
          style={{ fontFamily }}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <LogoUploader
              currentLogo={content.logo}
              defaultLogo={defaultLogo}
              onLogoChange={handleLogoChange}
              heightClass="h-8"
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              companyName={content.companyName || "HOK"}
              isEditing={!!onContentChange}
            />
            <button
              className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <PiXBold className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">
            <nav className="flex flex-col gap-4">
              {navItemsRaw.map((item: any, idx: number) => {
                const navItem =
                  typeof item === "string"
                    ? {
                        label: item,
                        sectionId: item.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
                      }
                    : item;

                const isCustomLink = navItem.sectionId === "custom";
                const targetHref = isCustomLink ? navItem.url : `#${navItem.sectionId}`;
                const isEditing = !!onContentChange;

                return (
                  <a
                    key={idx}
                    href={isEditing ? undefined : targetHref}
                    target={isCustomLink && !isEditing ? "_blank" : undefined}
                    rel={isCustomLink ? "noopener noreferrer" : undefined}
                    className="text-lg font-semibold border-b border-gray-50 pb-4 text-gray-800"
                    onClick={(e) => {
                      if (isEditing) {
                        e.preventDefault();
                      } else if (!isCustomLink) {
                        e.preventDefault();
                        setIsMobileMenuOpen(false);
                        const el = document.getElementById(navItem.sectionId);
                        if (el) {
                          el.scrollIntoView({ behavior: "smooth" });
                        }
                      } else {
                        setIsMobileMenuOpen(false);
                      }
                    }}
                  >
                    {navItem.label}
                  </a>
                );
              })}
            </nav>
            <div className="mt-auto pt-8 flex flex-col gap-4 border-t border-gray-100">
              <div className="w-full text-center py-2 border border-gray-200 rounded-lg font-semibold" style={{ color: primaryColor }}>
                <LoginDropdown variant="text" primaryColor={primaryColor} />
              </div>
              <button
                className="w-full rounded-lg px-6 py-3 text-sm font-bold text-white shadow-md transition-all hover:shadow-lg flex items-center justify-center gap-2"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                }}
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsSupportModalOpen(true);
                }}
              >
                <PiChatCircleTextBold className="w-4 h-4 text-white" />
                <span>{content.ctaText || "Help & Support"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Popup Modal */}
      {isSupportModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6"
          style={{ fontFamily }}
          onClick={() => setIsSupportModalOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-md" />

          {/* Modal */}
          <div
            className="relative w-full sm:max-w-2xl bg-white sm:rounded-3xl rounded-t-3xl shadow-[0_32px_80px_rgba(0,0,0,0.25)] overflow-hidden"
            style={{ animation: "modalIn 0.3s cubic-bezier(0.34,1.56,0.64,1)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative Header */}
            <div
              className="relative px-7 pt-8 pb-6 overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${primaryColor}f0, ${secondaryColor}e0)` }}
            >
              {/* Decorative orbs */}
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full opacity-20" style={{ background: "white" }} />
              <div className="absolute top-4 right-16 w-16 h-16 rounded-full opacity-10" style={{ background: "white" }} />

              <div className="relative flex items-start justify-between">
                <div>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-2">
                    <PiChatCircleTextBold className="w-3 h-3" />
                    {content.supportSubtitle || "Support Center"}
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight tracking-tight">
                    {content.supportTitle || "Get in Touch"}
                  </h2>
                  {content.supportDescription && (
                    <p className="text-sm text-white/70 mt-2 leading-relaxed max-w-sm">
                      {content.supportDescription}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setIsSupportModalOpen(false)}
                  className="flex-shrink-0 ml-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-all"
                  aria-label="Close"
                >
                  <PiXBold className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Cards Row */}
            <div className="p-6 sm:p-7 grid grid-cols-1 sm:grid-cols-3 gap-3">

              {/* Location Card */}
              <div className="flex flex-col rounded-2xl border border-gray-100 bg-gray-50/60 overflow-hidden hover:shadow-md transition-all duration-200 group">
                <div className="p-5 flex-1">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-sm"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                  >
                    <PiMapPin className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {content.supportLocationTitle || "Our Location"}
                  </p>
                  <p className="text-sm font-semibold text-gray-800 leading-relaxed whitespace-pre-line">
                    {content.supportLocation || "4517 Washington Ave. Manchester,\nKentucky 39495, USA"}
                  </p>
                </div>
                {content.supportLocationMapsUrl !== "" && (
                  <a
                    href={content.supportLocationMapsUrl || `https://maps.google.com/?q=${encodeURIComponent(content.supportLocation || "4517 Washington Ave. Manchester, Kentucky 39495, USA")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-3 px-5 text-xs font-bold border-t border-gray-100 text-white transition-all group-hover:opacity-90 cursor-pointer"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                  >
                    <PiMapPin className="w-3.5 h-3.5" />
                    Open in Maps
                    <PiArrowSquareOutBold className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>

              {/* Phone Card */}
              <div className="flex flex-col rounded-2xl border border-gray-100 bg-gray-50/60 overflow-hidden hover:shadow-md transition-all duration-200">
                <div className="p-5 flex-1">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-sm"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                  >
                    <PiPhone className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {content.supportPhoneTitle || "Phone Number"}
                  </p>
                  <p className="text-sm font-semibold text-gray-800 leading-relaxed whitespace-pre-line">
                    {content.supportPhone || "+(00) 12 - 345 6789\n+(00) 10 - 9876 543"}
                  </p>
                </div>
              </div>

              {/* Email Card */}
              <div className="flex flex-col rounded-2xl border border-gray-100 bg-gray-50/60 overflow-hidden hover:shadow-md transition-all duration-200">
                <div className="p-5 flex-1">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-sm"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                  >
                    <PiEnvelope className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {content.supportEmailTitle || "Email"}
                  </p>
                  <p className="text-sm font-semibold text-gray-800 leading-relaxed whitespace-pre-line">
                    {content.supportEmail || "team@example.com\nHoks@example.com"}
                  </p>
                </div>
              </div>

            </div>

            {/* Bottom drag indicator on mobile */}
            <div className="sm:hidden flex justify-center pb-4">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>
          </div>

          <style>{`
            @keyframes modalIn {
              from { opacity: 0; transform: scale(0.92) translateY(16px); }
              to   { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
